// Janelas dos conteúdos do Salão dos Portais: Torre Infinita, Labirinto do Caos e Wild Hunts.
// Visual de portais místicos (um vórtice com a cor de cada portal). CSS em client/css-parts/quests.css (prefixo .po-).
import { useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { LAB_TIME_MS, TOWER_CHECKPOINT, TOWER_LEVEL_PER_FLOOR, WILD_DAILY_MS, WILD_LOOT_MULT, WILD_POWER, WILD_XP_MULT, towerGold } from '../../../../shared/src/game/portal';

const n = (v: number) => v.toLocaleString('pt-BR');
const pct = (m: number) => `+${Math.round((m - 1) * 100)}%`;

/** "?" que abre sozinho na primeira visita (lembrado no navegador) */
function useHelp(key: string, auto: boolean): [boolean, () => void] {
  const seen = (() => { try { return localStorage.getItem(key) === '1'; } catch { return false; } })();
  const [v, setV] = useState<boolean | null>(null);
  const open = v ?? (auto && !seen);
  return [open, () => { setV(!open); try { localStorage.setItem(key, '1'); } catch { /* sem storage */ } }];
}

function Head(props: { glyph: string; title: string; sub: string; onHelp: () => void; children?: ComponentChildren }) {
  return (
    <div class="po-head">
      <div class="po-gate"><i class="po-swirl" /><span>{props.glyph}</span></div>
      <div class="po-head-txt">
        <div class="po-title">{props.title} <button class="po-help" onClick={props.onHelp} title="Como funciona">?</button></div>
        <div class="po-sub">{props.sub}</div>
        {props.children && <div class="po-tags">{props.children}</div>}
      </div>
    </div>
  );
}

function Door(props: { why: string; ok: string; label: string; disabled: boolean; onEnter: () => void }) {
  return (
    <div class="po-door">
      {props.why ? <span class="po-why">🔒 {props.why}</span> : <span class="po-go">✨ {props.ok}</span>}
      <button class="tb-button green po-enter" disabled={props.disabled} onClick={props.onEnter}>{props.label}</button>
    </div>
  );
}

export function Tower(props: { st: { best: number; start: number } | null; inCity: boolean; onEnter: () => void }) {
  const [help, toggleHelp] = useHelp('nx-help-tower', !!props.st && props.st.best === 0);
  if (!props.st) return <p class="muted">Carregando...</p>;
  const { best, start } = props.st;
  const nextGuard = (Math.floor(best / TOWER_CHECKPOINT) + 1) * TOWER_CHECKPOINT;
  const inBlock = best % TOWER_CHECKPOINT;
  return (
    <div class="po po-tower">
      <Head glyph="🗼" title="Torre Infinita" sub="Andares sem fim. Até onde você consegue subir?" onHelp={toggleHelp}>
        <span>Monstros de hunts level ~{TOWER_LEVEL_PER_FLOOR}×andar</span><span>Guardião a cada {TOWER_CHECKPOINT} andares</span><span>Sem limite de entradas</span>
      </Head>
      {help && (
        <div class="po-steps">
          <div><i>🚪</i><b>1. Entre</b><small>Cada andar tem uma leva de monstros, com XP e loot normais.</small></div>
          <div><i>⬆️</i><b>2. Suba</b><small>O andar N usa monstros das hunts de level ~{TOWER_LEVEL_PER_FLOOR}×N; depois da hunt mais alta, eles ficam mais fortes a cada andar.</small></div>
          <div><i>🛡️</i><b>3. Guardião</b><small>A cada {TOWER_CHECKPOINT} andares há um guardião e um ponto de retorno: a próxima entrada começa dali.</small></div>
        </div>
      )}
      <div class="po-stats">
        <div class="po-hi"><small>Seu recorde</small><b>{n(best)}</b><span>andar</span></div>
        <div><small>Próxima entrada</small><b>{n(start)}</b><span>andar de partida</span></div>
        <div class="po-gold"><small>Próximo recorde</small><b>{n(towerGold(best + 1))}<em>gp</em></b><span>andar {n(best + 1)}</span></div>
      </div>
      <div class="po-sec">Rumo ao guardião do andar {n(nextGuard)} <small>· faltam {n(nextGuard - best)} andares</small></div>
      <div class="po-floors">
        {Array.from({ length: TOWER_CHECKPOINT }, (_, i) => {
          const f = nextGuard - TOWER_CHECKPOINT + i + 1;
          const st = i < inBlock ? 'won' : i === inBlock ? 'next' : 'lock';
          return <span key={i} class={'po-floor ' + st + (i === TOWER_CHECKPOINT - 1 ? ' guard' : '')} title={`Andar ${f}`}>{i === TOWER_CHECKPOINT - 1 ? '🛡️' : st === 'won' ? '✔' : f}</span>;
        })}
      </div>
      <small class="po-note">Recorde novo: {n(2000)} gp × andar. A cada {TOWER_CHECKPOINT} andares: fragmentos e dust da Forge. Sem limite de entradas.</small>
      <Door why={props.inCity ? '' : 'Volte para a cidade para entrar.'} ok="O portal da torre está aberto." label={`Entrar na Torre (andar ${n(start)})`} disabled={!props.inCity} onEnter={props.onEnter} />
    </div>
  );
}

export function Labyrinth(props: { st: { left: number; total: number; tier: number; chestGold: number; gold: number } | null; inCity: boolean; onEnter: () => void }) {
  const [help, toggleHelp] = useHelp('nx-help-lab', !!props.st && props.st.left === props.st.total);
  if (!props.st) return <p class="muted">Carregando...</p>;
  const s = props.st;
  const min = LAB_TIME_MS / 60_000;
  return (
    <div class={'po po-lab' + (s.left ? '' : ' po-spent')}>
      <Head glyph="🌀" title="Labirinto do Caos" sub="Um labirinto diferente a cada dia da semana. O relógio não perdoa." onHelp={toggleHelp}>
        <span>Tier {s.tier}</span><span>⏳ {min} minutos</span><span>6 baús + Guardião</span>
      </Head>
      {help && (
        <div class="po-steps">
          <div><i>👥</i><b>1. Entre</b><small>Você (ou sua party, levada pelo líder) entra num labirinto com monstros da sua faixa de level (tier {s.tier}).</small></div>
          <div><i>🧰</i><b>2. Abra os baús</b><small>Atravesse os corredores e abra os 6 baús: {n(s.chestGold)} gp cada.</small></div>
          <div><i>👹</i><b>3. Guardião do Caos</b><small>Derrote-o na saída em até {min} minutos e leve {n(s.gold)} gp, fragmentos e um equipamento.</small></div>
        </div>
      )}
      <div class="po-stats">
        <div class={s.left ? '' : 'po-empty'}><small>Entradas hoje</small><b>{s.left}<em>/{s.total}</em></b>
          <span class="po-pips">{Array.from({ length: s.total }, (_, i) => <i key={i} class={i < s.left ? 'on' : ''} />)}</span></div>
        <div><small>Relógio</small><b>{min}<em>min</em></b><span>para atravessar</span></div>
        <div class="po-gold"><small>Cada baú</small><b>{n(s.chestGold)}<em>gp</em></b><span>× 6 baús</span></div>
        <div class="po-gold"><small>Guardião</small><b>{n(s.gold)}<em>gp</em></b><span>+ item</span></div>
      </div>
      <div class="po-chests">
        {Array.from({ length: 6 }, (_, i) => <span key={i} class="po-chest" title={`Baú ${i + 1}: ${n(s.chestGold)} gp`}>🧰<small>{n(s.chestGold)}</small></span>)}
        <span class="po-arrow">➜</span>
        <span class="po-chest boss" title={`Guardião do Caos: ${n(s.gold)} gp + item`}>👹<small>{n(s.gold)}</small></span>
      </div>
      <small class="po-note">O Guardião deixa 2 fragmentos e um equipamento do tier (Raro ou melhor) na Noxara Store Box. VIP: 2 entradas por dia.</small>
      <Door why={!props.inCity ? 'Volte para a cidade para entrar.' : !s.left ? 'Suas entradas de hoje acabaram.' : ''} ok="O labirinto muda de forma... entre se tiver coragem."
        label="Entrar no labirinto" disabled={!props.inCity || !s.left} onEnter={props.onEnter} />
    </div>
  );
}

export function Wild(props: { st: { band: string; pool: string[]; online: number; left?: number } | null; inCity: boolean; onEnter: () => void }) {
  const [help, toggleHelp] = useHelp('nx-help-wild', !!props.st && (props.st.left ?? WILD_DAILY_MS) >= WILD_DAILY_MS);
  if (!props.st) return <p class="muted">Carregando...</p>;
  const s = props.st;
  const total = WILD_DAILY_MS / 60_000;
  const leftMin = Math.ceil((s.left ?? 0) / 60_000);
  const out = (s.left ?? 1) <= 0;
  return (
    <div class={'po po-wild' + (out ? ' po-spent' : '')}>
      <Head glyph="🐾" title="Wild Hunts" sub="Um descampado aberto onde todos da mesma faixa caçam juntos." onHelp={toggleHelp}>
        <span>Levels {s.band}</span><span>Monstros trocam toda segunda</span><span>{pct(WILD_XP_MULT)} XP · {pct(WILD_LOOT_MULT)} loot</span>
      </Head>
      {help && (
        <div class="po-steps">
          <div><i>🌾</i><b>1. Entre</b><small>Um descampado com monstros misturados da sua faixa de level. Todos da mesma faixa caçam juntos.</small></div>
          <div><i>💪</i><b>2. Mais perigo</b><small>Monstros {pct(WILD_POWER)} mais fortes e monstros especiais (Super, Mega e Ultra) com o triplo da chance.</small></div>
          <div><i>⏱️</i><b>3. 1 hora por dia</b><small>Seu tempo zera à meia-noite de Brasília.</small></div>
        </div>
      )}
      <div class="po-stats">
        <div><small>Sua faixa</small><b>{s.band}</b><span>levels</span></div>
        <div class="po-live"><small>Caçando agora</small><b>{n(s.online)}</b><span>{s.online === 1 ? 'herói' : 'heróis'}</span></div>
        <div class={out ? 'po-empty' : ''}><small>Seu tempo hoje</small><b>{leftMin}<em>/{total} min</em></b><span>restantes</span></div>
        <div class="po-gold"><small>Bônus</small><b>{pct(WILD_XP_MULT)}<em>XP</em></b><span>{pct(WILD_LOOT_MULT)} loot</span></div>
      </div>
      <div class="po-time" title={`${leftMin} de ${total} minutos`}><i style={{ width: `${Math.max(0, Math.min(100, (leftMin / total) * 100))}%` }} /><span>{out ? 'Tempo de hoje esgotado' : `${leftMin} de ${total} minutos restantes hoje`}</span></div>
      <div class="po-sec">Monstros da semana <small>· {pct(WILD_POWER)} mais fortes</small></div>
      <div class="po-pool">{s.pool.map((m) => <span key={m}>{m}</span>)}{!s.pool.length && <small class="muted">Nenhum monstro nesta faixa.</small>}</div>
      <small class="po-note">Os monstros são {pct(WILD_POWER)} mais fortes e os monstros especiais (Super, Mega e Ultra) aparecem com o triplo da chance. Cada personagem pode ficar 1 hora por dia nas Wild Hunts; o tempo zera à meia-noite de Brasília.</small>
      <Door why={!props.inCity ? 'Volte para a cidade para entrar.' : out ? 'Seu tempo de hoje acabou. Volta à meia-noite.' : ''} ok="O descampado está aberto: a caçada começou."
        label={out ? 'Tempo de hoje esgotado' : 'Entrar nas Wild Hunts'} disabled={!props.inCity || out} onEnter={props.onEnter} />
    </div>
  );
}
